
import React from 'react';
import { Link } from 'react-router-dom';
import { useCommunity } from '@/contexts/CommunityContext';
import { Facebook, Twitter, Instagram } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslation } from 'react-i18next';

const Footer: React.FC = () => {
  const { currentCommunity } = useCommunity();
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  return (
    <footer className="bg-gray-100 text-gray-800 mt-6 md:mt-10 pb-16 lg:pb-0">
      <div className="container mx-auto px-4 py-6 lg:py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {/* Community Info */}
          <div>
            <Link to="/" className="mb-3 block">
              <div className="font-semibold text-xl text-gray-900">{currentCommunity.name}</div>
            </Link>
            <p className="text-gray-600 mb-4 max-w-xs text-sm md:text-base">
              {currentCommunity.description && currentCommunity.description.length > 120 
                ? `${currentCommunity.description.substring(0, 120)}...` 
                : currentCommunity.description}
            </p>
            
            {/* Social Links */}
            <div className="flex space-x-3 mt-3">
              {currentCommunity.facebook_url && (
                <a 
                  href={currentCommunity.facebook_url} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full bg-white hover:bg-gray-200 transition-colors text-gray-700 hover:text-gray-900"
                  aria-label="facebook link"
                >
                  <Facebook size={18} />
                </a>
              )}
              {currentCommunity.twitter_url && (
                <a 
                  href={currentCommunity.twitter_url} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full bg-white hover:bg-gray-200 transition-colors text-gray-700 hover:text-gray-900"
                  aria-label="twitter link"
                >
                  <Twitter size={18} />
                </a>
              )}
              {currentCommunity.instagram_url && (
                <a 
                  href={currentCommunity.instagram_url} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full bg-white hover:bg-gray-200 transition-colors text-gray-700 hover:text-gray-900"
                  aria-label="instagram link"
                >
                  <Instagram size={18} />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links and Legal side by side on mobile */}
          <div className="grid grid-cols-2 gap-4 col-span-1 md:col-span-2">
            {/* Quick Links */}
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">{t('footer.links.title')}</h3>
              <ul className="space-y-2">
                <li>
                  <Link 
                    to="/" 
                    className="text-gray-600 hover:text-gray-900 transition-colors text-sm md:text-base"
                  >
                    {t('footer.links.home')}
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/services" 
                    className="text-gray-600 hover:text-gray-900 transition-colors text-sm md:text-base"
                  >
                    {t('footer.links.services')}
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/about" 
                    className="text-gray-600 hover:text-gray-900 transition-colors text-sm md:text-base"
                  >
                    {t('footer.links.about')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">{t('footer.legal.title')}</h3>
              <ul className="space-y-2">
                <li>
                  <Link 
                    to="/terms" 
                    className="text-gray-600 hover:text-gray-900 transition-colors text-sm md:text-base"
                  >
                    {t('footer.terms')}
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/privacy" 
                    className="text-gray-600 hover:text-gray-900 transition-colors text-sm md:text-base"
                  >
                    {t('footer.privacy')}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <Separator className="my-4 md:my-6 bg-gray-300" />
        
        <div className="text-center text-gray-600 text-xs md:text-sm">
          <p>{t('footer.copyright', { year: new Date().getFullYear(), community: currentCommunity.name })}</p>
          <p 
            className="mt-2"
            dangerouslySetInnerHTML={{ 
              __html: t('footer.developed_by', { 
                link: '<a href="https://treerootssoftware.co.uk" target="_blank" rel="noopener noreferrer" class="text-primary hover:text-primary/80 underline transition-colors">TreeRoots Software</a>'
              }) || 'Developed by <a href="https://treerootssoftware.co.uk" target="_blank" rel="noopener noreferrer" class="text-primary hover:text-primary/80 underline transition-colors">TreeRoots Software</a>'
            }} 
          />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
